import { z } from "zod";
import { MAX_WHITEBOARD_TOMBSTONES, whiteboardTombstoneSchema } from "@/lib/whiteboard/merge";

export const WHITEBOARD_MAX_BYTES = 10 * 1024 * 1024;
export const whiteboardTitleSchema = z.string().trim().min(1).max(120);

// Preserve the editor's versioned JSON fields, while bounding the scene and
// rejecting unsupported embeds and malformed geometry before storing it.
export const whiteboardElementSchema = z
  .object({
    id: z.string().min(1).max(200),
    type: z.enum([
      "rectangle",
      "diamond",
      "ellipse",
      "arrow",
      "line",
      "freedraw",
      "text",
      "image",
      "frame",
    ]),
    x: z.number().finite(),
    y: z.number().finite(),
    width: z.number().nonnegative().finite(),
    height: z.number().nonnegative().finite(),
    angle: z.number().finite(),
    version: z.number().int().nonnegative(),
  })
  .catchall(z.json());

export const WHITEBOARD_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
] as const;

export type WhiteboardImageType = (typeof WHITEBOARD_IMAGE_TYPES)[number];

/** One uploaded image, decoded (its data URL is 4/3 of this, within the old 10 MiB scene limit). */
export const WHITEBOARD_FILE_MAX_BYTES = 7.5 * 1024 * 1024;

const fileIdSchema = z.string().min(1).max(200);

/** An image with its bytes inline: the editor's own form, drafts, imports and exports. */
export const whiteboardDataFileSchema = z.object({
  id: fileIdSchema,
  mimeType: z.enum(WHITEBOARD_IMAGE_TYPES),
  dataURL: z
    .string()
    .max(WHITEBOARD_MAX_BYTES)
    .regex(/^data:image\/(?:png|jpeg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=\s]+$/),
  created: z.number().nonnegative().finite().optional(),
});

/**
 * An image uploaded to the board (`PUT /api/whiteboards/$id/files/$fileId`):
 * the scene keeps only its reference. Strict, so a data file whose data URL is
 * invalid is refused instead of passing as a reference without its bytes.
 */
export const whiteboardFileRefSchema = z
  .object({
    id: fileIdSchema,
    mimeType: z.enum(WHITEBOARD_IMAGE_TYPES),
    created: z.number().nonnegative().finite().optional(),
  })
  .strict();

/** A stored or sent image: always a reference, its bytes are in R2. */
export const whiteboardFileSchema = whiteboardFileRefSchema;

const sceneFields = {
  elements: z.array(whiteboardElementSchema).max(10_000),
  appState: z.object({ gridModeEnabled: z.boolean(), objectsSnapModeEnabled: z.boolean() }),
  version: z.number().finite(),
};

/** The scene as stored, read and saved: images are references. */
export const whiteboardSceneSchema = z.object({
  ...sceneFields,
  files: z.array(whiteboardFileSchema).max(100),
});

/** The scene as the editor holds it, with every image inline: drafts, imports, exports. */
export const whiteboardClientSceneSchema = z.object({
  ...sceneFields,
  files: z.array(whiteboardDataFileSchema).max(100),
});

export const whiteboardLibrarySchema = z
  .array(
    z.object({
      id: z.string().min(1).max(200),
      name: z.string().max(120).optional(),
      created: z.number().nonnegative().finite(),
      elements: z.array(whiteboardElementSchema).max(1_000),
    }),
  )
  .max(100);

export type WhiteboardScene = z.infer<typeof whiteboardSceneSchema>;

export type WhiteboardClientScene = z.infer<typeof whiteboardClientSceneSchema>;

export type WhiteboardStoredFile = z.infer<typeof whiteboardFileSchema>;

export type WhiteboardDataFile = z.infer<typeof whiteboardDataFileSchema>;

export type WhiteboardFileRef = z.infer<typeof whiteboardFileRefSchema>;

/** The scene's images as references, for saves and comparisons. */
export const fileRef = ({
  id,
  mimeType,
  created,
}: WhiteboardDataFile | WhiteboardFileRef): WhiteboardFileRef =>
  created === undefined ? { id, mimeType } : { id, mimeType, created };

export type WhiteboardLibrary = z.infer<typeof whiteboardLibrarySchema>;

export function emptyWhiteboardScene(): WhiteboardScene {
  return {
    elements: [],
    files: [],
    appState: { gridModeEnabled: false, objectsSnapModeEnabled: false },
    version: 0,
  };
}

const titleDate = new Intl.DateTimeFormat("fr-CA", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "America/Montreal",
});

/** « Tableau du 12 octobre 2026 »: new boards stay distinguishable in the list. */
export function defaultWhiteboardTitle(date: Date) {
  return `Tableau du ${titleDate.format(date)}`;
}

/** A title nobody chose: the dated default or the former « Tableau sans titre ». */
export function isDefaultWhiteboardTitle(title: string) {
  return /^Tableau (sans titre|du \d{1,2} \p{L}+ \d{4})$/u.test(title);
}

/** « Copie de … », kept within the title limit. */
export function copyWhiteboardTitle(title: string) {
  return `Copie de ${title}`.slice(0, 120).trim();
}

const projectIdSchema = z.string().trim().min(1).max(64);

export const createWhiteboardSchema = z.object({
  title: whiteboardTitleSchema,
  /** Copies this board's drawing and library (own or shared with the requester). */
  sourceId: z.uuid().optional(),
  projectId: projectIdSchema.optional(),
});

export const updateWhiteboardSchema = z
  .object({
    revision: z.number().int().positive(),
    title: whiteboardTitleSchema.optional(),
    scene: whiteboardSceneSchema.optional(),
    /** The scene's deletions. Sending them merges the scene element by element instead of
     * replacing it, so editors drawing at the same time keep each other's work. */
    deleted: z.array(whiteboardTombstoneSchema).max(MAX_WHITEBOARD_TOMBSTONES).optional(),
    library: whiteboardLibrarySchema.optional(),
    /** A chantier the owner belongs to, or null to unlink. */
    projectId: projectIdSchema.nullable().optional(),
  })
  .refine(
    (value) =>
      value.title !== undefined ||
      value.scene !== undefined ||
      value.library !== undefined ||
      value.projectId !== undefined,
    "Aucune modification.",
  )
  .refine(
    (value) => new TextEncoder().encode(JSON.stringify(value)).byteLength <= WHITEBOARD_MAX_BYTES,
    "Le tableau dépasse la limite de 10 Mo.",
  );

export const deleteWhiteboardSchema = z.object({
  revision: z.number().int().positive(),
  discardEmpty: z.literal(true).optional(),
});

export type WhiteboardUpdate = z.infer<typeof updateWhiteboardSchema>;

export const WHITEBOARD_PREVIEW_MAX_BYTES = 1024 * 1024;

/** The list preview of one revision; null when the board is empty. */
export const whiteboardPreviewSchema = z.object({
  revision: z.number().int().positive(),
  preview: z
    .string()
    .max(WHITEBOARD_PREVIEW_MAX_BYTES)
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/)
    .nullable(),
});

export const whiteboardPermissionSchema = z.enum(["viewer", "editor"]);

export type WhiteboardPermission = z.infer<typeof whiteboardPermissionSchema>;

export const whiteboardShareSchema = z.object({
  userId: z.string().min(1).max(200),
  permission: whiteboardPermissionSchema.default("viewer"),
});

export const whiteboardShareUpdateSchema = whiteboardShareSchema.extend({
  permission: whiteboardPermissionSchema,
});

const projectSchema = z.object({ id: z.string(), name: z.string(), projectNumber: z.string() });

export type WhiteboardProject = z.infer<typeof projectSchema>;

export const whiteboardSummarySchema = z.object({
  id: z.uuid(),
  title: z.string(),
  revision: z.number().int(),
  updatedAt: z.iso.datetime(),
  project: projectSchema.nullable(),
});

const accessSchema = {
  externalShared: z.boolean(),
  /** Editors change the drawing; only owners manage the board and its access. */
  access: z.enum(["owner", "viewer", "editor"]),
  owner: z.object({ id: z.string(), name: z.string() }),
};

/** A card of « Mes tableaux ». */
export const whiteboardListItemSchema = whiteboardSummarySchema.extend({
  ...accessSchema,
  elementCount: z.number().int().nonnegative(),
  /** Version of the stored preview, null before the first one. */
  previewAt: z.iso.datetime().nullable(),
  /** People the owner shared it with; 0 for a viewer. */
  shareCount: z.number().int().nonnegative(),
});

export const whiteboardDetailSchema = whiteboardSummarySchema.extend({
  ...accessSchema,
  scene: whiteboardSceneSchema,
  /** What was deleted from the scene, so a live tab can drop what it missed. */
  deleted: z.array(whiteboardTombstoneSchema).optional(),
  library: whiteboardLibrarySchema,
  /** Revision the stored preview shows; the editor refreshes a missing or stale one. */
  previewRevision: z.number().int().nullable(),
});

export const whiteboardShareEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  /** False when the person lacks the tableau-blanc flag and cannot open it yet. */
  hasAccess: z.boolean(),
  permission: whiteboardPermissionSchema,
});

export type WhiteboardSummary = z.infer<typeof whiteboardSummarySchema>;

export type WhiteboardListItem = z.infer<typeof whiteboardListItemSchema>;

export type WhiteboardDetail = z.infer<typeof whiteboardDetailSchema>;

export type WhiteboardShareEntry = z.infer<typeof whiteboardShareEntrySchema>;
